import { ObjectHelpers } from "../helpers/object-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { TemporaryStopLossModel } from "../models/stop-loss/temporary-stop-loss.model";
import { IComponent } from "./component.interface";
import { ExitOrderPlotLineComponent } from "./exit-order-plot-line.component";
import { InformationPopupComponent } from "./information-popup.component";
import { PnlComponent } from "./pnl.component";

export class DrawExitOrderComponent implements IComponent {
    public id = 'draw-exit-order-' + StringHelpers.newGuid();

    public exitOrderPlotLine: ExitOrderPlotLineComponent | null = null;
    public removeProfitTargetOcaGroups: Array<string> = [];
    public removeStopLossOcaGroups: Array<string> = [];
    public requestingProfitTarget = false;
    public requestingStopLoss = false;

    private base: IChartContainer;

    private tradeOrders: Array<TradeOrderDataModel> = [];
    private currentPositions: Array<CurrentPositionViewModel> = [];

    private prevTradeOrders: Array<TradeOrderDataModel> = [];
    private prevCurrentPositions: Array<CurrentPositionViewModel> = [];

    private chartStyle: ChartStyleSettingsModel | null = null;
    private canDraw = false;
    private chartLoaded = false;
    // public rightEdge = false;
    // public extreamSet = false;
    private isRemovingForSwitchingSymbol = false;
    private drawTimer: any;
    private redrawTimer: any;

    private temporaryStopLosses: Array<TradeOrderDataModel> = [];

    public rightEdgeWidth = 0;
    public initialize = false;

    constructor(base: IChartContainer) {
        this.base = base;

        if (!this.base.marketingWebChartMode) {
            this.canDraw = true;
        }

        this.subscribeChartDataService();
        this.subscribeChartEvents();
        this.onResize();
    }

    private remove() {
        this.isRemovingForSwitchingSymbol = true;
        this.removePlotLine();
        this.currentPositions = [];
        this.removeProfitTargetOcaGroups = [];
        this.removeStopLossOcaGroups = [];
        this.requestingProfitTarget = false;
        this.requestingStopLoss = false;
        this.chartLoaded = false;
        this.rightEdgeWidth = 0;
        // this.extreamSet = false;
        // this.rightEdge = false;
        this.temporaryStopLosses = [];
        this.isRemovingForSwitchingSymbol = false;
        this.initialize = false;
        this.disable();
    }

    public enable() {
        if (!this.base.marketingWebChartMode) return;

        if (this.canDraw) return;

        this.canDraw = true;
    }

    public disable() {
        if (!this.base.marketingWebChartMode) return;

        this.canDraw = false;
    }

    private addTemporaryStopLoss(model: TemporaryStopLossModel) {
        if (model.symbol != this.base.tiChartSeries.symbol) return;

        const index = this.temporaryStopLosses.findIndex(x => x.accountName == model.accountName && x.temporaryStopLoss.stopType == model.stopType);
        const m = new TradeOrderDataModel({
            accountName: model.accountName,
            price: model.price,
            temporaryStopLoss: model,
            isTemporary: true,
            entryTime: model.entryTime
        });;

        if (index > -1) {
            this.temporaryStopLosses[index] = m;
        } else {
            this.temporaryStopLosses.push(m);
        }

        this.addTemporaryStopLossPlotLines();
    }

    public removeTemporaryStopLoss(accountName: string) {
        if (this.temporaryStopLosses && this.temporaryStopLosses.some((x: TradeOrderDataModel) => x.accountName == accountName)) {
            this.exitOrderPlotLine.removeStopLosses();
            this.temporaryStopLosses = [];
        }
    }

    private subscribeChartDataService() {
        this.base.chartService.subscribeChartRequestingNewSymbolData(() => {
            this.remove();
        });
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            if (this.isRemovingForSwitchingSymbol) {
                return;
            }

            this.tradeOrders = tradeOrders;
            this.displayProfitTargetAndStopLoss();

            this.prevTradeOrders = ObjectHelpers.copy(tradeOrders);
        });
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;

            if (!this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry) {
                this.remove();
            }
        });
        this.base.chartService?.subscribe_AddTemporaryStopLoss((data: TemporaryStopLossModel) => {
            if (this.isRemovingForSwitchingSymbol) {
                return;
            }

            this.addTemporaryStopLoss(data);
        });
        this.base.chartService?.subscribe_RemoveTemporaryStopLoss((data: any) => {
            this.removeTemporaryStopLoss(data.accountName);
        });
        this.base.chartService?.subscribeCurrentPositionViewModel((currentPositions: Array<CurrentPositionViewModel>) => {

            if (this.isRemovingForSwitchingSymbol) {
                return;
            }

            this.currentPositions = currentPositions;
            this.update();

            this.prevCurrentPositions = ObjectHelpers.copy(currentPositions);
        });
    }

    private subscribeChartEvents() {
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.exitOrderPlotLine?.onExtreamChange();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.exitOrderPlotLine?.onRedraw();
        });
        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.exitOrderPlotLine?.onLastPriceChange(lastPrice);
        });
        this.base.tiChartSeries?.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                this.chartLoaded = true;
                this.update();
            }, 500);
        });
    }

    public update(forceHide?: boolean) {
        // if (rightEdge) {
        //     this.rightEdge = true;
        //     this.extreamSet = false;
        // }

        if (!this.canDraw || !this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry) return;

        if (this.exitOrderPlotLine) {
            const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
            if (anyPlotLineMoving) {
                return;
            }

            if (this.exitOrderPlotLine.disabled) {
                const key = this.exitOrderPlotLine.position.getKey();
                const position = this.currentPositions.find(x => x.getKey() === key);
                if (position && position.shares != 0) {
                    return;
                }
            }
        }

        if (forceHide) {
            this.removePlotLine();
            this.removeLastPriceElements();
            return;
        }


        this.draw();
        clearTimeout(this.redrawTimer);
        this.redrawTimer = setTimeout(() => {
            this.base.tiChartSeries?.highStockChart?.redraw();
        }, 50);
        clearTimeout(this.drawTimer);
        this.drawTimer = setTimeout(() => {
            this.draw();
        }, 500);
        // setTimeout(() => {
        //     if (this.rightEdge && !this.extreamSet && this.base.tiChartSeries?.ready) {
        //         this.base.tiChartSeries?.setExtreamToExitOrderIfExist();
        //         this.extreamSet = true;
        //     }
        // }, 100);
    }

    private draw() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (cannotDraw) {
            return;
        }

        this.cancelStopLossOrProfitTargetIfFilled();

        this.removePlotLine();
        this.createPlotLines();

        this.rightEdgeWidth = this.exitOrderPlotLine?.elementWidth ?? 0;
        
        if (!this.initialize) {
            setTimeout(() => {
                this.initialize = true;
            }, 500);
        }
    }

    private canDrawExitPlotLine(): boolean {
        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (this.exitOrderPlotLine?.mouseHover || !this.chartLoaded || !this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry || this.base.tiChartSeries.preventDragging || popup.isActive()) {
            // console.log('canDrawExitPlotLine false', ' mouseHover:', this.exitOrderPlotLine?.mouseHover, ' chartLoaded: ', this.chartLoaded, ' defaultEnableTradeOrderEntry: ', !this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry, ' preventDragging: ', this.base.tiChartSeries.preventDragging, ' isActive: ', popup.isActive())

            if (JSON.stringify(this.prevTradeOrders) != JSON.stringify(this.tradeOrders)
                || JSON.stringify(this.prevCurrentPositions) != JSON.stringify(this.currentPositions)) {
                // console.log('canDrawExitPlotLine true because data changed');
                return true;
            }

            return false;
        }

        return true;
    }

    private cancelStopLossOrProfitTargetIfFilled() {
        if (!this.exitOrderPlotLine) {
            return;
        }

        const positionExist = this.currentPositions.find(x => x.accountId == this.exitOrderPlotLine.position.accountId && x.shares > 0);
        if (positionExist) {
            return;
        }

        const tradeOrders = this.getSLOrPTTradeOrders().filter(x => x.statusText?.toLowerCase() == 'filled');
        for (const t of tradeOrders) {
            let tradeType = '';
            if (t.tradeType?.toLowerCase() == 'limit') {
                tradeType = 'stop';
            } else if (t.tradeType?.toLowerCase() == 'stop') {
                tradeType = 'limit';
            }

            const relatedOrders = this.getSLOrPTTradeOrders().filter(x =>
                x.tradeType?.toLowerCase() == tradeType &&
                x.statusText?.toLowerCase() == 'submitted');

            for (const relatedOrder of relatedOrders) {
                relatedOrder.statusText = 'canceled';
                this.base.chartService.requestRemoveChartTrade(relatedOrder.id);
            }
        }
    }

    private removePlotLine() {
        if (this.exitOrderPlotLine) {
            this.exitOrderPlotLine.remove(true);
            this.exitOrderPlotLine = null;
            // console.log('removePlotLine 1')
        }
    }

    private removeLastPriceElements() {
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.exit-order').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
            el.remove();
        });
    }

    private createPlotLines() {
        // console.log('createPlotLines 1')
        const pnlComponent = this.base.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
        let selectedAccount = '';
        if (pnlComponent) {
            selectedAccount = pnlComponent.selectedAccount;
        }

        let positionToAdd: CurrentPositionViewModel = null;
        for (const position of this.currentPositions) {
            if (position.shares != 0 && selectedAccount == position.accountName) {
                positionToAdd = position;
                break;
            }
        }

        if (positionToAdd) {
            // console.log('createPlotLines 2', positionToAdd);
            const order = this.getOrder(positionToAdd);
            this.exitOrderPlotLine = new ExitOrderPlotLineComponent(this.base, this, positionToAdd, order);
            this.displayProfitTargetAndStopLoss();
        } else {
            this.removeLastPriceElements();
        }
    }

    public displayProfitTargetAndStopLoss() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (!this.exitOrderPlotLine || cannotDraw) return;

        const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
        if (anyPlotLineMoving) {
            return;
        }

        let profitTarget: TradeOrderDataModel | null = null;
        let stopLoss: TradeOrderDataModel | null = null;
        if (this.exitOrderPlotLine.isLong) {
            profitTarget = this.getOrder_LongProfitTarget();
            stopLoss = this.getOrder_LongStopLoss();
        } else {
            profitTarget = this.getOrder_ShortProfitTarget();
            stopLoss = this.getOrder_ShortStopLoss();
        }

        if (profitTarget && this.removeProfitTargetOcaGroups.indexOf(profitTarget.ocaGroup) == -1) {
            this.exitOrderPlotLine.createProfitTargetFromTradeOrder(profitTarget);

            this.requestingProfitTarget = false;
        } else {
            this.exitOrderPlotLine.removeProfitTarget();
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
                el.remove();
            });
        }

        if (stopLoss && this.removeStopLossOcaGroups.indexOf(stopLoss.ocaGroup) == -1) {
            this.exitOrderPlotLine.createStopLossFromTradeOrder(stopLoss);
            this.requestingStopLoss = false;
        } else if (this.temporaryStopLosses?.length > 0) {
            this.addTemporaryStopLossPlotLines();
        } else {
            this.exitOrderPlotLine.removeStopLosses();
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
                el.remove();
            });
        }
    }

    private addTemporaryStopLossPlotLines() {
        const cannotDraw = !this.canDrawExitPlotLine();
        if (!this.exitOrderPlotLine?.position || cannotDraw) return;

        const anyPlotLineMoving = this.exitOrderPlotLine.anyPlotLinesMoving();
        if (anyPlotLineMoving) {
            return;
        }

        const stopLosees = this.temporaryStopLosses.filter(x => x.accountName == this.exitOrderPlotLine.position.accountName);
        this.exitOrderPlotLine.createTemporaryStopLoss(stopLosees);
        this.requestingStopLoss = true;
    }

    private getOrder(p: CurrentPositionViewModel): TradeOrderDataModel | null {
        const tradeOrders = this.tradeOrders.sort((a, b) => {
            return (b.entryTime?.valueOf() ?? -99999) - (a.entryTime?.valueOf() ?? -99999);
        });

        const tradeOrder = tradeOrders.find(x =>
            x.accountName == p.accountName &&
            !x.ocaGroup);
        return tradeOrder;
    }

    private getOrder_LongProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'limit' &&
            !x.isLong);
        return tradeOrder;
    }

    private getOrder_LongStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'stop' &&
            !x.isLong);
        return tradeOrder;
    }

    private getOrder_ShortProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'limit' &&
            x.isLong);
        return tradeOrder;
    }

    private getOrder_ShortStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getSLOrPTTradeOrders().find(x =>
            x.tradeType?.toLowerCase() == 'stop' &&
            x.isLong);
        return tradeOrder;
    }

    private getSLOrPTTradeOrders(): Array<TradeOrderDataModel> {
        if (!this.exitOrderPlotLine?.position) return [];

        const order = this.exitOrderPlotLine.model;
        const position = this.exitOrderPlotLine.position;
        const ocaGroup = position.getOCAGroupPrefix();
        const tradeOrders = this.tradeOrders
            .filter(x => x.accountName == position.accountName && (x.ocaGroup.indexOf(ocaGroup) > -1 || (order && x.ocaGroup == order.id)));
        return tradeOrders;
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.update();
            }, 150);
        });
    }
}